export type DebugLevel = 'info' | 'warn' | 'error';

export interface DebugEvent {
  id: number;
  ts: number;
  iso: string;
  level: DebugLevel;
  channel: string;
  event: string;
  data?: unknown;
}

interface DebugReport {
  version: number;
  generatedAt: string;
  sessionId: string;
  debugEnabled: boolean;
  location: {
    href: string;
    origin: string;
    pathname: string;
    search: string;
    userAgent: string;
    language: string;
  };
  env: {
    dev: boolean;
    apiBase: string;
  };
  events: DebugEvent[];
  extra?: unknown;
}

const MODE_KEY = 'wavedrop_debug_mode';
const SESSION_KEY = 'wavedrop_debug_session_id';
const MAX_EVENTS = 8000;

let initialized = false;
let seq = 1;
const events: DebugEvent[] = [];
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((fn) => fn());
}

function safeClone(data: unknown): unknown {
  if (data === undefined) return undefined;
  try {
    return JSON.parse(JSON.stringify(data, (_k, value) => {
      if (value instanceof Error) {
        return {
          name: value.name,
          message: value.message,
          stack: value.stack,
        };
      }
      if (typeof value === 'bigint') return value.toString();
      if (typeof value === 'function') return `[Function ${value.name || 'anonymous'}]`;
      return value;
    }));
  } catch {
    return String(data);
  }
}

function ensureSessionId(): string {
  if (typeof window === 'undefined') return 'server';
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = `dbg_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

function installGlobalErrorHooks(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (ev) => {
    logDebug('runtime', 'window.error', {
      message: ev.message,
      filename: ev.filename,
      lineno: ev.lineno,
      colno: ev.colno,
    }, 'error');
  });
  window.addEventListener('unhandledrejection', (ev) => {
    logDebug('runtime', 'window.unhandledrejection', {
      reason: safeClone(ev.reason),
    }, 'error');
  });
}

export function initializeDebugMode(): void {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  const params = new URLSearchParams(window.location.search);
  const raw = (params.get('debug') ?? '').toLowerCase();
  if (raw === '1' || raw === 'true' || raw === 'on') {
    localStorage.setItem(MODE_KEY, '1');
  } else if (raw === '0' || raw === 'false' || raw === 'off') {
    localStorage.removeItem(MODE_KEY);
  }

  ensureSessionId();
  installGlobalErrorHooks();
  logDebug('debug', 'mode.initialized', {
    enabled: isDebugModeEnabled(),
    href: window.location.href,
  });
}

export function isDebugModeEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  initializeDebugMode();
  return localStorage.getItem(MODE_KEY) === '1';
}

export function getDebugSessionId(): string {
  initializeDebugMode();
  return ensureSessionId();
}

export function logDebug(
  channel: string,
  event: string,
  data?: unknown,
  level: DebugLevel = 'info',
): void {
  if (!isDebugModeEnabled()) return;
  const now = Date.now();
  events.push({
    id: seq++,
    ts: now,
    iso: new Date(now).toISOString(),
    level,
    channel,
    event,
    data: safeClone(data),
  });
  if (events.length > MAX_EVENTS) {
    events.splice(0, events.length - MAX_EVENTS);
  }
  notify();
}

export function getDebugEventCount(): number {
  return events.length;
}

export function onDebugEvent(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function clearDebugEvents(): void {
  events.length = 0;
  notify();
}

export function buildDebugReport(extra?: unknown): DebugReport {
  initializeDebugMode();
  const href = typeof window !== 'undefined' ? window.location.href : '';
  const url = href ? new URL(href) : new URL('https://wavedrop.local/');
  return {
    version: 1,
    generatedAt: new Date().toISOString(),
    sessionId: getDebugSessionId(),
    debugEnabled: isDebugModeEnabled(),
    location: {
      href: url.href,
      origin: url.origin,
      pathname: url.pathname,
      search: url.search,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      language: typeof navigator !== 'undefined' ? navigator.language : '',
    },
    env: {
      dev: !!import.meta.env.DEV,
      apiBase: import.meta.env.VITE_API_URL ?? '',
    },
    events: [...events],
    extra: safeClone(extra),
  };
}

export function downloadDebugReport(opts?: { filePrefix?: string; extra?: unknown }): void {
  if (typeof window === 'undefined') return;
  const report = buildDebugReport(opts?.extra);
  const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
  const a = document.createElement('a');
  const ts = new Date().toISOString().replace(/[:.]/g, '-');
  const prefix = opts?.filePrefix ?? 'wavedrop-debug';
  a.href = URL.createObjectURL(blob);
  a.download = `${prefix}-${report.sessionId}-${ts}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

